/**
 * O que as telas da fase 6 do espelho têm em comum (19/10/2026): carregar, atualizar sozinha a
 * cada minuto, e andar de mês em mês.
 *
 * É o mesmo jeito do Fechamento da congregação, da fase 5: a atualização da MESMA folha mantém os
 * números na tela enquanto busca os novos; trocar de folha (outra congregação, outro mês) limpa e
 * mostra "Carregando...". No papel, nada se atualiza.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor } from '../api'
import { competenciaPorExtenso, mesAnterior, mesSeguinte } from '../dinheiro'
import { useAtualizarSozinho, type Atualizacao } from './AtualizadoAs'

export function useTelaDoMes<T>(
  /** A chave da folha: muda quando é outra folha, e não quando é a mesma atualizada. */
  folha: string,
  buscar: () => Promise<T>,
  aoSair: () => void,
  paraImprimir: boolean
): { dados: T | null; erro: string | null; atualizacao: Atualizacao } {
  const [dados, setDados] = useState<T | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const atualizacao = useAtualizarSozinho(!paraImprimir)
  const { rodada, marcarAtualizado } = atualizacao
  const qualFolha = useRef('')
  const aBusca = useRef(buscar)
  aBusca.current = buscar

  useEffect(() => {
    let vale = true
    setErro(null)
    if (qualFolha.current !== folha) setDados(null)
    qualFolha.current = folha

    aBusca.current()
      .then((recebido) => {
        if (!vale) return
        setDados(recebido)
        marcarAtualizado()
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [folha, aoSair, rodada, marcarAtualizado])

  return { dados, erro, atualizacao }
}

/** As setas do mês, como nas outras folhas. O que vier de filho entra ao lado, como o PDF. */
export function NavegarMes({
  mes,
  andar,
  children
}: {
  mes: string
  andar: (para: string) => void
  children?: React.ReactNode
}): JSX.Element {
  return (
    <nav className="navegar-mes so-tela">
      <button aria-label="Mês anterior" onClick={() => andar(mesAnterior(mes))}>◀</button>
      <strong>{competenciaPorExtenso(mes)}</strong>
      <button aria-label="Mês seguinte" onClick={() => andar(mesSeguinte(mes))}>▶</button>
      {children}
    </nav>
  )
}
